import { NomidioChannel } from '@shared/api/identity/models/nomidio-channel';
import { createNilReducer, createReducer, dispatch, select } from '../core';
import { AccessToken } from '@shared/api/common/access-token';
import { RootState } from '../store';
import { IdentifierStatus } from '@shared/api/identity/models/invitation-identifier-status.enum';
import { OptStatus } from '@shared/models/opt-status.enum';
import { StepStatus } from '../models/step-status';

export type RegisterState = {
  /** A draft of a potential registration that will be set up once the user consents. */
  draft: RegistrationDraft | undefined;
  /** The current registration being worked on. */
  current: CurrentRegistration | undefined;
};

/**
 * A draft of a potential registration the user may want to complete.
 * This can be requested by e.g. login, and is converted into an actual registration
 * when the user initially agrees to register.
 */
export type RegistrationDraft = {
  /** An ID to unambiguously identify when this draft is supposed to be used. If there is an ID mismatch, it will not be used. */
  id: string;
  /** An email that should be used for registration. Note that it will always be unverified. */
  email?: string;
  enterpriseName?: string;
  /** The planned exit for the registration. */
  exit?: RegistrationExit;
  /** Any tokens that can/should be carried over to the registration. */
  tokens: Partial<RegistrationTokens>;
}

/**
 * A current and ongoing registration the user is following through.
 */
export type CurrentRegistration = {
  /** This registration's unique ID. */
  id: string;
  /** The ID of the draft used to create this registration, if any. */
  draftId?: string;
  /** The ISO timestamp at which this registration was created. */
  createdAt: string;
  /** The ISO timestamp at which this registration will expire. */
  expiresAt: string;
  /** Miscellaneous information about this registration */
  info: {
    isSelfRegistration: boolean;
    isFromLogin: boolean;
    channel?: NomidioChannel;
    enterpriseName?: string;
  };
  /** If this is a self-registration, details about that self-registration will be here. */
  self?: {
    userId?: string;
    domain?: string;
  }
  /** The exit to follow once this registration terminates. */
  exit?: RegistrationExit;
  tokens: {
    check?: AccessToken;
    identity?: AccessToken;
    mfa?: AccessToken;
  };
  /**
   * The list of steps involved in this registration.
   * This list is ordered. Changing the order of steps will change the order they're completed in.
   */
  steps: RegisterStep[];
};

/**
 * A registration exit describes alternate locations the registration should end, beside the account area.
 * This can influence error handling.
 */
export type RegistrationExit =
  | {
      type: 'auth';
      authCode: string;
    }
  | {
      type: 'oauth';
      /** OAuth registrations will have an auth code when coming from login. */
      authCode?: string;
      openIdServer: string;
      applicationName: string;
    }
  | {
      type: 'redirect';
      redirectUrl: string;
    }
  | {
      type: 'phone';
      dialledNumber: string;
    };

type TncState = { general: boolean; terms: boolean; privacy: boolean };
type TokenName = 'mfa' | 'identity' | 'check';
export type RegistrationTokens = Record<TokenName, AccessToken>;
type TokenUpdate = Partial<RegistrationTokens>;

export type RegisterStepType = RegisterStep['type'];
export type ExactRegisterStep<T extends RegisterStepType> = RegisterStep & { type: T };
type RegisterStepArgs<T extends RegisterStepType> = ExactRegisterStep<T>['args'];

export type RegisterStep = {
  status: StepStatus;
  optional: boolean;
  args?: {};
  data?: {};
} & (
  | {
      type: 'consent';
      args: {
        tncState: TncState;
      };
      data?: {
        userId?: string;
        dialledNumber?: string;
        consentedToEnrol: boolean;
        tncState: TncState;
      };
    }
  | {
      type: 'personal_details';
      data?: {
        firstName: string;
        lastName: string;
      };
    }
  | {
      type: 'email';
      args: {
        maskedEmail?: string;
        forbiddenEmails: string[];
      };
      data?: {
        unverifiedEmail: string;
      };
    }
  | {
      type: 'verify_email';
      args: {
        mfaVerificationId?: string;
      };
      data?: {
        email: string;
        mfaProof?: string;
      };
    }
  | {
      type: 'phone';
      args: {
        maskedPhoneNumber?: string;
        forbiddenPhoneNumbers: string[];
      };
      data?: {
        unverifiedPhoneNumber: string;
      };
    }
  | {
      type: 'verify_phone';
      args: {
        mfaVerificationId?: string;
      };
      data?: {
        phoneNumber: string;
        mfaProof?: string;
      };
    }
  | {
      type: 'create_self_registration';
      args: {
        unsupportedBrowser?: boolean;
      }
      data?: {
        userId: string;
      };
    }
  | {
      type: 'face_training';
    }
  | {
      type: 'voice_training';
      data?: {
        locale: string;
        verified: boolean;
      };
    }
  | {
      type: 'iproov_opt_in';
      data?: {
        iproovOptStatus: OptStatus;
      };
    }
  | {
      type: 'face_verify';
      args: {
        method: 'standard' | 'iproov';
      };
      data?: {
        verified: boolean;
      };
    }
  | {
      type: 'webauthn';
    }
  | {
      type: 'avatar';
      data?: {
        image: string;
      };
    }
  | {
      type: 'totp';
    }
  | {
      type: 'summary';
      args: {
        accountCreated?: boolean;
        enrolled?: boolean;
      };
    }
  | {
      type: 'additional_setup';
    }
);

function updateTokens(reg: CurrentRegistration, update: TokenUpdate) {
  if (update.check) {
    reg.tokens.check = update.check;
  }
  if (update.identity) {
    reg.tokens.identity = update.identity;
  }
  if (update.mfa) {
    reg.tokens.mfa = update.mfa;
  }
}

export function newRegisterState(): RegisterState {
  return {
    draft: undefined,
    current: undefined,
  };
}

type NewRegisterStepConfig<T extends RegisterStepType> = {
  type: T;
  args: RegisterStepArgs<T>;
  optional?: boolean;
};
export function newRegisterStep<T extends RegisterStepType = 'consent'>(config: NewRegisterStepConfig<T>) {
  return {
    type: config.type,
    args: config.args,
    optional: config.optional ?? false,
    status: StepStatus.Idle,
  };
}

function newStandardRegistrationSteps() {
  return [
    newRegisterStep({ type: 'consent', args: { tncState: { general: false, terms: false, privacy: false } } }),
    newRegisterStep({ type: 'personal_details', args: {} }),
    newRegisterStep({ type: 'email', args: { forbiddenEmails: [] } }),
    newRegisterStep({ type: 'verify_email', args: {} }),
    newRegisterStep({ type: 'phone', args: { forbiddenPhoneNumbers: [] } }),
    newRegisterStep({ type: 'verify_phone', args: {} }),
    newRegisterStep({ type: 'face_training', args: {} }),
    newRegisterStep({ type: 'iproov_opt_in', args: {} }),
    newRegisterStep({ type: 'face_verify', args: { method: 'standard' } }),
    newRegisterStep({ type: 'voice_training', args: {} }),
    newRegisterStep({ type: 'webauthn', args: {} }),
    newRegisterStep({ type: 'summary', args: {} }),
  ];
}

function newSelfRegistrationSteps() {
  return [
    newRegisterStep({ type: 'consent', args: { tncState: { general: false, terms: false, privacy: false } } }),
    newRegisterStep({ type: 'email', args: { forbiddenEmails: [] } }),
    newRegisterStep({ type: 'verify_email', args: {} }),
    newRegisterStep({ type: 'phone', args: { forbiddenPhoneNumbers: [] } }),
    newRegisterStep({ type: 'verify_phone', args: {} }),
    newRegisterStep({ type: 'create_self_registration', args: {} }),
    newRegisterStep({ type: 'personal_details', args: {} }),

    // standard steps resume from here
    newRegisterStep({ type: 'face_training', args: {} }),
    newRegisterStep({ type: 'iproov_opt_in', args: {} }),
    newRegisterStep({ type: 'face_verify', args: { method: 'standard' } }),
    newRegisterStep({ type: 'voice_training', args: {} }),
    newRegisterStep({ type: 'webauthn', args: {} }),
    newRegisterStep({ type: 'summary', args: {} }),
  ]
}

function newRegistration(config: Pick<CurrentRegistration, 'id' | 'draftId' | 'createdAt' | 'expiresAt' | 'info'>): CurrentRegistration {
  return {
    ...config,
    exit: undefined,
    tokens: {},
    steps: [],
  };
}

export const loadRegisterSlice = createReducer((state, payload: RegisterState) => {
  state.register = {
    ...newRegisterState(),
    ...payload,
  }
});

type StartRegistrationOptions = {
  /** The new registration's ID. */
  id: string;
  /** The draft ID used to create this registration, if there was one. */
  draftId?: string;
  createdAt: string;
  expiresAt: string;
  channel?: NomidioChannel;
  enterpriseName?: string;
  isFromLogin?: boolean;
  isSelfRegistration?: boolean;
  selfRegistrationInfo?: Exclude<CurrentRegistration["self"], undefined>;
};
/**
 * Start a new registration.
 *
 * This will clear any existing registration draft. Retrieve it first if you want to use its data.
 */
export const startRegistration = createReducer((state, payload: StartRegistrationOptions) => {
  const {
    id,
    draftId,
    createdAt,
    expiresAt,
    channel,
    enterpriseName,
    isFromLogin = false,
    isSelfRegistration = false,
    selfRegistrationInfo = undefined,
  } = payload;

  const reg = {
    ...newRegistration({
      id,
      draftId,
      createdAt,
      expiresAt,
      info: {
        isSelfRegistration,
        isFromLogin,
        channel,
        enterpriseName,
      },
    }),
    self: selfRegistrationInfo,
    steps: isSelfRegistration ? newSelfRegistrationSteps() : newStandardRegistrationSteps(),
  }

  state.register.current = reg;
  state.register.draft = undefined;
});

export type StartRegistrationDraftOptions = {
  id: string;
  email?: string;
  enterpriseName?: string;
  exit?: RegistrationExit;
  tokens?: Partial<RegistrationTokens>;
}
export const startRegistrationDraft = createReducer((state, payload: StartRegistrationDraftOptions) => {
  state.register.draft = {
    id: payload.id,
    email: payload.email,
    exit: payload.exit,
    enterpriseName: payload.enterpriseName,
    tokens: {
      ...payload.tokens,
    }
  };
});

/**
 * Delete the current registration.
 *
 * This drops it from records entirely. You will be unable to access it.
 */
export const deleteCurrentRegistration = createNilReducer((state) => {
  state.register.current = undefined;
});

/** Provide a prepared valid registration exit for this registration. */
export const markRegistrationExit = createReducer((state, payload: { exit: RegistrationExit }) => {
  const { exit } = payload;
  const reg = select(state, selectCurrentRegistration());
  if (!reg || !exit) {
    return;
  }
  reg.exit = payload.exit;
})

/** Mark this registration as an OAuth registration. */
export const markRegistrationIsOauth = createReducer((state, payload: { applicationName: string; openIdServer: string, authCode?: string }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.exit = {
    type: 'oauth',
    authCode: payload.authCode,
    applicationName: payload.applicationName,
    openIdServer: payload.openIdServer,
  };
});

/** Mark this registration as an auth registration. */
export const markRegistrationIsAuth = createReducer((state, payload: { authCode: string }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.exit = {
    type: 'auth',
    authCode: payload.authCode,
  };
});

/** Mark this registration as having a redirect at the end. */
export const markRegistrationRedirectUrl = createReducer((state, payload: { redirectUrl: string }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.exit = {
    type: 'redirect',
    redirectUrl: payload.redirectUrl,
  };
});

/** Mark this registration as having come from a specific dialled number. */
export const markRegistrationDialledNumber = createReducer((state, payload: { dialledNumber: string }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.exit = {
    type: 'phone',
    dialledNumber: payload.dialledNumber,
  };
});

export const markRegistrationIsUnsupportedBrowser = createNilReducer((state) => {
  const step = select(state, selectRegistrationStep("create_self_registration"));
  step.args.unsupportedBrowser = true;
})

// We plan to merge self-registration into the register, just with possibly differently ordered steps or arguments.
// Don't spend too much time on the import self-registration feature.
type ImportSelfRegistrationOptions = Omit<StartRegistrationOptions, 'id' | 'channel'>;
/** Import a self-registration (from the `/registration` route) into registration state. */
export const importSelfRegistration = createReducer((state, payload: ImportSelfRegistrationOptions) => {
  dispatch(
    state,
    startRegistration({
      id: 'self',
      channel: undefined,
      ...payload,
    }),
  );
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.info.isSelfRegistration = true;
});

type UpdateArgsPayload<T extends RegisterStepType = any> = { type: T; args: Partial<RegisterStepArgs<T>> };
/**
 * Update the arguments of a given step.
 *
 * This will add to the current arguments.
 */
export const updateRegistrationStepArgs = createReducer((state: RootState, payload: UpdateArgsPayload) => {
  const { type, args } = payload;
  const step = select(state, selectRegistrationStep(type));
  if (!step) {
    console.warn(`Instructed to set args for step "${step}", but no such step exists in this registration`);
    return;
  }
  step.args = {
    ...(step.args ?? {}),
    ...args,
  };
})
/**
 * Mark a registration step as complete, and store any data and/or tokens from that step.
 */
export const completeRegistrationStep = createReducer((state, payload: Pick<RegisterStep, 'type' | 'data'> & { tokens?: TokenUpdate }) => {
  const { type, data, tokens } = payload;
  const step = select(state, selectRegistrationStep(type));
  if (!step) {
    console.warn(`Instructed to complete step "${step}", but no such step exists in this registration`);
    return;
  }
  step.status = StepStatus.Complete;
  if (data) {
    step.data = data;
  }
  if (tokens) {
    const reg = select(state, selectCurrentRegistration());
    if (!reg) {
      return;
    }
    updateTokens(reg, tokens);
  }
});

/**
 * Mark a registration step as skipped.
 */
export const skipRegistrationStep = createReducer((state, payload: { type: RegisterStepType }) => {
  const { type } = payload;
  const step = select(state, selectRegistrationStep(type));
  if (!step) {
    console.warn(`Instructed to skip step "${step}", but no such step exists in this registration`);
    return;
  }
  step.status = StepStatus.Skipped;
});

/**
 * Roll back registration steps to a certain point.
 *
 * The `rollbackTo` target you specify is left untouched. Everything after that point is marked incomplete.
 */
export const rollbackRegistrationSteps = createReducer((state, payload: { rollbackTo: RegisterStepType }) => {
  const { rollbackTo } = payload;
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }

  let reset = false;
  for (const step of reg.steps) {
    if (step.type === rollbackTo) {
      reset = true;
      continue;
    }

    if (reset) {
      step.status = StepStatus.Idle;
    }
  }
});

/**
 * Mark a registration step as failed.
 */
export const failRegistrationStep = createReducer((state, payload: { type: RegisterStepType }) => {
  const { type } = payload;
  const step = select(state, selectRegistrationStep(type));
  if (!step) {
    console.warn(`Instructed to fail step "${step}", but no such step exists in this registration`);
    return;
  }
  step.status = StepStatus.Failed;
});

/**
 * Remove phone steps from the current registration.
 *
 * This is needed when phone is disabled in config.
 */
export const removePhoneSteps = createNilReducer((state) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  reg.steps = reg.steps.filter((s) => {
    const remove: RegisterStepType[] = ['phone', 'verify_phone'];
    return !remove.includes(s.type);
  });
});

/**
 * Mark phone steps as optional for this registration.
 */
export const markPhoneStepsOptional = createNilReducer((state) => {
  const phone = select(state, selectRegistrationStep('phone'));
  const verifyPhone = select(state, selectRegistrationStep('verify_phone'));

  if (phone) {
    phone.optional = true;
  }
  if (verifyPhone) {
    verifyPhone.optional = true;
  }
});

/**
 * Add the additional setup steps that come after summary.
 *
 * This is used (or not) depending on deployment config.
 */
export const addRegistrationAdditionalSetup = createNilReducer((state) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return;
  }
  if (reg.steps.find((s) => s.type === 'additional_setup')) {
    console.warn('Cannot add additional setup, it already exists');
    return;
  }
  reg.steps = [
    ...reg.steps,
    newRegisterStep({ type: 'additional_setup', args: {} }),
    newRegisterStep({ type: 'totp', args: {}, optional: true }),
    newRegisterStep({ type: 'avatar', args: {}, optional: true }),
  ];
});

/**
 * Prepare the phone or email verification step.
 */
export const prepareMfaAuthentication = createReducer(
  (state, payload: { type: 'verify_phone' | 'verify_email'; mfaVerificationId: string }) => {
    const { type, mfaVerificationId } = payload;
    const step = select(state, selectRegistrationStep(type));
    if (!step) {
      return;
    }
    step.status = StepStatus.Idle;
    step.data = undefined;
    step.args = { mfaVerificationId };
  },
);

export type RegistrationIdentifers = {
  phone_number: string;
  email: string;
  phone_status: IdentifierStatus;
  email_status: IdentifierStatus;
};
/**
 * Store pre-existing identifiers (phone or email) for the registration.
 */
export const storeRegistrationIdentifiers = createReducer((state, payload: { identifiers: RegistrationIdentifers }) => {
  const { identifiers } = payload;

  const phone = select(state, selectRegistrationStep('phone'));
  const verifyPhone = select(state, selectRegistrationStep('verify_phone'));
  if (phone && verifyPhone) {
    if (identifiers.phone_status === IdentifierStatus.Verified) {
      phone.status = StepStatus.Complete;
      verifyPhone.status = StepStatus.Complete;
      verifyPhone.data = {
        phoneNumber: identifiers.phone_number,
      };
    } else {
      phone.args.maskedPhoneNumber = identifiers.phone_number;
    }
  }

  const email = select(state, selectRegistrationStep('email'));
  const verifyEmail = select(state, selectRegistrationStep('verify_email'));
  if (email && verifyEmail) {
    if (identifiers.email_status === IdentifierStatus.Verified) {
      email.status = StepStatus.Complete;
      verifyEmail.status = StepStatus.Complete;
      verifyEmail.data = {
        email: identifiers.email,
      };
    } else {
      email.args.maskedEmail = identifiers.email;
    }
  }
});

/** Add one or more access tokens to the current registration. */
export const putRegistrationTokens = createReducer((state, payload: TokenUpdate) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    console.warn("Cannot put registration tokens; registration doesn't exist");
    return;
  }
  updateTokens(reg, payload);
});

/** Delete a token from the current registration. */
export const deleteRegistrationToken = createReducer((state, payload: { name: TokenName }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    console.warn("Cannot delete registration tokens; registration doesn't exist");
    return;
  }
  reg.tokens[payload.name] = undefined;
});

/**
 * Set an unverifed email for the `'email'` step, without completing or altering that step.
 *
 * This is used for self-registration to pre-seed your email field.
 */
export const setUnverifiedEmail = createReducer((state, payload: { email: string }) => {
  const emailStep = select(state, selectRegistrationStep("email"));
  emailStep.data = {
    unverifiedEmail: payload.email,
  }
})

/**
 * Set the registration enterprise name.
 *
 * This is needed in self-registration.
 */
export const setEnterpriseName = createReducer((state, payload: { enterpriseName: string }) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    console.warn("Cannot set enterprise name; registration doesn't exist");
    return;
  }
  reg.info.enterpriseName = payload.enterpriseName;
});

export const addForbiddenEmail = createReducer((state, payload: { email: string }) => {
  const { email } = payload;
  const step = select(state, selectRegistrationStep("email"));
  if (!step.args.forbiddenEmails.includes(email)) {
    step.args.forbiddenEmails.push(email);
  }
});

export const addForbiddenPhoneNumber = createReducer((state, payload: { phoneNumber: string }) => {
  const { phoneNumber } = payload;
  const step = select(state, selectRegistrationStep("phone"));
  if (!step.args.forbiddenPhoneNumbers.includes(phoneNumber)) {
    step.args.forbiddenPhoneNumbers.push(phoneNumber);
  }
});

export const redoRegistrationEmail = createNilReducer((state) => {
  const step = select(state, selectRegistrationStep("email"));
  const verify = select(state, selectRegistrationStep("verify_email"));

  step.status = StepStatus.Idle;
  step.data = undefined;

  verify.status = StepStatus.Idle;
  verify.args.mfaVerificationId = undefined;
  verify.data = undefined;
});

export const redoRegistrationPhone = createNilReducer((state) => {
  const step = select(state, selectRegistrationStep("phone"));
  const verify = select(state, selectRegistrationStep("verify_phone"));

  step.status = StepStatus.Idle;
  step.data = undefined;

  verify.status = StepStatus.Idle;
  verify.args.mfaVerificationId = undefined;
  verify.data = undefined;
});

// Selectors

/**
 * Get the current registration in progress.
 */
export const selectCurrentRegistration = () => (state: RootState) => {
  return state.register.current;
};

/**
 * Get the current registration draft.
 */
export const selectRegistrationDraft = () => (state: RootState) => {
  return state.register.draft;
};

/**
 * Get a specific step from the current registration in progress.
 */
export const selectRegistrationStep =
  <T extends RegisterStepType>(type: T) =>
  (state: RootState) => {
    return state.register.current?.steps.find((step) => step.type === type) as ExactRegisterStep<T>;
  };

/**
 * Select the next idle step in registration.
 */
export const selectNextRegistrationStep = () => (state: RootState) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return undefined;
  }
  return reg.steps.find((s) => s.status === StepStatus.Idle);
};

/**
 * Get an access token stored for this registration.
 */
export const selectRegistrationToken =
  (token: TokenName) =>
  (state: RootState): AccessToken | undefined => {
    const reg = select(state, selectCurrentRegistration());
    if (!reg) {
      return undefined;
    }
    switch (token) {
      case 'check':
        return reg.tokens.check;
      case 'identity':
        return reg.tokens.identity;
      case 'mfa':
        return reg.tokens.mfa;
    }
  };

/**
 * Select the user ID currently stored for this registration.
 *
 * This is available after the consent step is complete.
 */
export const selectRegistrationUserId = () => (state: RootState) => {
  const consent = select(state, selectRegistrationStep('consent'));
  const create = select(state, selectRegistrationStep('create_self_registration'));
  return consent?.data?.userId ?? create?.data?.userId ?? undefined;
};

export const selectRegistrationExit = () => (state: RootState) => {
  const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return undefined;
  }
  return reg.exit;
};

export const selectRegistrationAuthCode = () => (state: RootState) => {
   const reg = select(state, selectCurrentRegistration());
  if (!reg) {
    return undefined;
  };
  const { exit } = reg;
  if (!exit) {
    return undefined;
  }
  return "authCode" in exit ? exit.authCode : undefined;
}
